import { useState } from 'react';
import { useRunExecution } from '@/hooks/useRunExecution';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Loader2, Play, RefreshCw, Square, Zap } from 'lucide-react';
import { RunProgressPanel } from '@/components/run/RunProgressPanel';
import { RunEventTimeline } from '@/components/run/RunEventTimeline';
import { RequirementsViewer } from '@/components/run/RequirementsViewer';
import { JsonBlock, ArtifactItem, ErrorBox } from '@/components/debug/DebugHelpers';
import { config } from '@/lib/config';
import type { CreateRunPayload } from '@/lib/reqqyBackend';

export default function DebugExecution() {

  const {
    runId, run, events, artifacts,
    isCreating, isExecuting, isPolling,
    error,
    startRun, startPolling, stopPolling, refreshRun,
  } = useRunExecution();

  const [projectId, setProjectId] = useState('demo-project');
  const [moduleType, setModuleType] = useState('generation');
  const [rawIdea, setRawIdea] = useState('A mobile app that helps users track their water intake throughout the day with smart reminders and weekly reports.');
  const [productName, setProductName] = useState('HydroTrack');

  const handleCreateAndExecute = () => {
    const payload: CreateRunPayload = {
      module_type: moduleType as CreateRunPayload['module_type'],
      project_id: projectId,
      input_payload: {
        product_name: productName,
        raw_idea: rawIdea,
      },
      context_payload: {},
      config_payload: {
        depth: 'standard',
        output_style: 'structured',
      },
    };
    startRun(payload);
  };

  const isBusy = isCreating || isExecuting;

  // Find the requirements artifact
  const reqArtifact = artifacts.find(a => a.artifact_type === 'requirements_document');
  const reqDoc = reqArtifact?.content_json as Record<string, unknown> | undefined;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-display font-bold">Execution Debug Console</h1>
        <p className="text-sm text-muted-foreground">
          Backend: <code className="bg-muted px-1 rounded">{config.backendUrl}</code>
        </p>
      </div>

      {/* ── Input form ── */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Create & Execute Run</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-muted-foreground">Project ID</label>
              <Input value={projectId} onChange={e => setProjectId(e.target.value)} />
            </div>
            <div>
              <label className="text-xs text-muted-foreground">Module Type</label>
              <Input value={moduleType} onChange={e => setModuleType(e.target.value)} />
            </div>
          </div>
          <div>
            <label className="text-xs text-muted-foreground">Product Name</label>
            <Input value={productName} onChange={e => setProductName(e.target.value)} />
          </div>
          <div>
            <label className="text-xs text-muted-foreground">Raw Idea / Input</label>
            <Textarea rows={4} value={rawIdea} onChange={e => setRawIdea(e.target.value)} />
          </div>

          <div className="flex gap-2 flex-wrap">
            <Button onClick={handleCreateAndExecute} disabled={isBusy}>
              {isBusy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Play className="w-4 h-4 mr-2" />}
              {isCreating ? 'Creating…' : isExecuting ? 'Executing…' : 'Create & Execute'}
            </Button>

            {runId && (
              <>
                <Button variant="outline" size="sm" onClick={refreshRun}>
                  <RefreshCw className="w-4 h-4 mr-1" /> Refresh
                </Button>
                {isPolling ? (
                  <Button variant="outline" size="sm" onClick={stopPolling}>
                    <Square className="w-4 h-4 mr-1" /> Stop Polling
                  </Button>
                ) : (
                  <Button variant="outline" size="sm" onClick={() => startPolling(runId)}>
                    <Zap className="w-4 h-4 mr-1" /> Start Polling
                  </Button>
                )}
              </>
            )}
          </div>

          {error && <ErrorBox error={error} />}
        </CardContent>
      </Card>

      {/* ── Run state ── */}
      {run && (
        <div className="grid lg:grid-cols-2 gap-6">
          {/* Left: Progress + Summary */}
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  Run Progress
                  {isPolling && <Badge variant="secondary" className="text-xs">Polling</Badge>}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <RunProgressPanel run={run} />
                <div className="mt-4 grid grid-cols-2 gap-2 text-xs">
                  <div><span className="text-muted-foreground">ID:</span> <code className="break-all">{run.id}</code></div>
                  <div><span className="text-muted-foreground">Module:</span> {run.module_type}</div>
                  <div><span className="text-muted-foreground">Created:</span> {run.created_at ? new Date(run.created_at).toLocaleString() : '—'}</div>
                  <div><span className="text-muted-foreground">Started:</span> {run.started_at ? new Date(run.started_at).toLocaleString() : '—'}</div>
                  <div><span className="text-muted-foreground">Completed:</span> {run.completed_at ? new Date(run.completed_at).toLocaleString() : '—'}</div>
                </div>
              </CardContent>
            </Card>

            {/* Events */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Events ({events.length})</CardTitle>
              </CardHeader>
              <CardContent className="max-h-80 overflow-auto">
                <RunEventTimeline events={events} />
              </CardContent>
            </Card>
          </div>

          {/* Right: Payloads + Artifacts */}
          <div className="space-y-6">
            {/* Payloads */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Payloads</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {(['input_payload', 'context_payload', 'config_payload', 'result_payload', 'metrics_payload', 'error_payload'] as const).map(key => {
                  const data = run[key] as Record<string, unknown> | undefined;
                  const isEmpty = !data || Object.keys(data).length === 0;
                  return (
                    <details key={key} className="text-sm">
                      <summary className="cursor-pointer py-1 font-medium flex items-center gap-2">
                        {key}
                        {isEmpty && <span className="text-xs text-muted-foreground">(empty)</span>}
                      </summary>
                      {!isEmpty && <JsonBlock data={data} />}
                    </details>
                  );
                })}
              </CardContent>
            </Card>

            {/* Artifacts */}
            {artifacts.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Artifacts ({artifacts.length})</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  {artifacts.map((a, i) => (
                    <ArtifactItem key={a.id ?? i} artifact={a as any} />
                  ))}
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      )}

      {/* ── Requirements Viewer ── */}
      {reqDoc && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">📄 Generated Requirements</CardTitle>
          </CardHeader>
          <CardContent>
            <RequirementsViewer data={reqDoc as any} />
          </CardContent>
        </Card>
      )}
    </div>
  );
}
